import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Magnification levels a click or double tap steps through before returning to fitted size. */
const ZOOM_STEPS = [2.5, 4];

/**
 * The picture is never magnified past the last step. The stage is 584px across and the
 * source image is 3840px, so beyond this there are no real pixels left to show and the
 * label text the shopper came to read only gets softer.
 */
const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1];

/** Two taps on a touch screen closer together than this count as one double tap. */
const DOUBLE_TAP_MS = 300;

/** A press that travels further than this is a drag rather than a click. */
const TAP_SLOP = 6;

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} gallery - The scrollable single-image carousel.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} [prevButton] - Previous-image arrow (absent when there is a single media).
 * @property {HTMLButtonElement} [nextButton] - Next-image arrow (absent when there is a single media).
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'gallery', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** The index of the image currently shown in the stage. */
  #activeIndex = 0;

  /** How far the visible picture is magnified, and how far it has been dragged. */
  #scale = 1;
  #panX = 0;
  #panY = 0;

  /** Pointers currently down on the picture, keyed by pointer id. */
  #pointers = /** @type {Map<number, {x: number, y: number}>} */ (new Map());

  /** Where a pan began and the offset it started from. Null unless a drag can pan. */
  #drag = /** @type {{x: number, y: number, panX: number, panY: number} | null} */ (null);

  /** The spread and midpoint of a two-finger pinch as of the last move. */
  #pinch = /** @type {{spread: number, x: number, y: number} | null} */ (null);

  /** Where a press started, used to tell a click apart from a drag. */
  #pressedAt = /** @type {{x: number, y: number} | null} */ (null);
  #dragged = false;
  #lastTapAt = 0;

  connectedCallback() {
    super.connectedCallback();
    const { gallery } = this.refs;

    // The carousel (not the dialog) is the horizontal scroller across all breakpoints.
    gallery.addEventListener('scroll', this.handleScroll);

    // Zoom listens on the carousel rather than on each image: the images are swapped for
    // higher-resolution copies as those load, and anything bound to an image element
    // would be thrown away with it.
    gallery.addEventListener('pointerdown', this.#handlePointerDown);
    gallery.addEventListener('pointermove', this.#handlePointerMove);
    gallery.addEventListener('pointerup', this.#handlePointerEnd);
    gallery.addEventListener('pointercancel', this.#handlePointerEnd);
    gallery.addEventListener('wheel', this.#handleWheel, { passive: false });
    gallery.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    gallery.addEventListener('dragstart', this.#handleDragStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { gallery } = this.refs;

    gallery.removeEventListener('scroll', this.handleScroll);
    gallery.removeEventListener('pointerdown', this.#handlePointerDown);
    gallery.removeEventListener('pointermove', this.#handlePointerMove);
    gallery.removeEventListener('pointerup', this.#handlePointerEnd);
    gallery.removeEventListener('pointercancel', this.#handlePointerEnd);
    gallery.removeEventListener('wheel', this.#handleWheel);
    gallery.removeEventListener('touchmove', this.#handleTouchMove);
    gallery.removeEventListener('dragstart', this.#handleDragStart);
  }

  // ---------------------------------------------------------------------------
  // Zoom
  //
  // Magnification is a CSS transform on the image itself, so the slide keeps its size
  // and the carousel underneath carries on working untouched. One piece of state --
  // whether the picture is magnified -- settles every gesture that could mean two
  // things: at fitted size a drag moves between images, and once magnified it pans.
  // ---------------------------------------------------------------------------

  /**
   * The slide currently in view.
   * @returns {HTMLElement | null}
   */
  #activeSlide() {
    return this.refs.media?.[this.#activeIndex] ?? null;
  }

  /**
   * The picture currently in view, or null when the slide holds a video or a model.
   * Looked up on each use because higher-resolution copies replace the element as they
   * load, leaving any earlier reference pointing at a detached image.
   * @returns {HTMLImageElement | null}
   */
  #activeImage() {
    const slide = this.#activeSlide();
    if (!slide?.classList.contains('product-media-container--image')) return null;

    const image = slide.querySelector('img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * Records which slide is in view. The picture always goes back to its fitted size when
   * the slide changes, so the next image never opens part-way cropped.
   * @param {number} index - The index of the slide now in view.
   */
  #showIndex(index) {
    if (index !== this.#activeIndex) this.#resetZoom();

    this.#activeIndex = index;
    // Video and model slides cannot be magnified, so they carry no zoom affordance.
    this.toggleAttribute('data-zoomable', this.#activeImage() != null);
  }

  /**
   * Returns the picture to its fitted size and drops any gesture in progress.
   */
  #resetZoom() {
    const image = this.#activeImage();
    if (image) image.style.transform = '';

    this.#scale = 1;
    this.#panX = 0;
    this.#panY = 0;
    this.#pointers.clear();
    this.#drag = null;
    this.#pinch = null;
    this.#pressedAt = null;
    this.#dragged = false;
    this.removeAttribute('data-zoomed');
    this.removeAttribute('data-panning');
  }

  /**
   * How far the picture may be dragged before its edge would come inside the stage.
   * Measured against the drawn picture rather than the slide, because a portrait image is
   * letterboxed and panning to the slide's edge would pull it into empty space.
   * @param {HTMLImageElement} image - The picture being panned.
   * @returns {{x: number, y: number}} The furthest offset allowed on each axis.
   */
  #panLimit(image) {
    const boxWidth = image.offsetWidth;
    const boxHeight = image.offsetHeight;
    const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
    const drawnWidth = Math.min(boxWidth, boxHeight * ratio);
    const drawnHeight = Math.min(boxHeight, boxWidth / ratio);

    return {
      x: Math.max(0, (drawnWidth * this.#scale - boxWidth) / 2),
      y: Math.max(0, (drawnHeight * this.#scale - boxHeight) / 2),
    };
  }

  /**
   * Writes the current magnification and offset onto the visible picture.
   */
  #render() {
    const image = this.#activeImage();
    if (!image) return;

    const limit = this.#panLimit(image);
    this.#panX = Math.min(limit.x, Math.max(-limit.x, this.#panX));
    this.#panY = Math.min(limit.y, Math.max(-limit.y, this.#panY));

    image.style.transform =
      this.#scale === 1 ? '' : `translate(${this.#panX}px, ${this.#panY}px) scale(${this.#scale})`;
    this.toggleAttribute('data-zoomed', this.#scale > 1);
  }

  /**
   * Magnifies to a new level while holding one point still, so whatever sits under the
   * cursor or between the fingers stays where it was.
   * @param {number} scale - The magnification asked for, before it is capped.
   * @param {number} focusX - Distance of the held point from the centre of the stage.
   * @param {number} focusY - Distance of the held point from the centre of the stage.
   */
  #zoomTo(scale, focusX, focusY) {
    const next = Math.min(MAX_ZOOM, Math.max(1, scale));
    const ratio = next / this.#scale;

    this.#scale = next;
    this.#panX = focusX + (this.#panX - focusX) * ratio;
    this.#panY = focusY + (this.#panY - focusY) * ratio;

    if (next === 1) {
      this.#panX = 0;
      this.#panY = 0;
    }

    this.#render();
  }

  /**
   * Steps to the next magnification level and wraps back to fitted size. A click is the
   * only way in for a desktop mouse, which has no pinch, so it has to reach every level
   * on its own.
   * @param {PointerEvent} event - The click or tap that asked for it.
   */
  #stepZoom(event) {
    const focus = this.#focusOf(event.clientX, event.clientY);
    const next = ZOOM_STEPS.find((step) => step > this.#scale + 0.01) ?? 1;

    this.#zoomTo(next, focus.x, focus.y);
  }

  /**
   * Where a point on the screen sits relative to the centre of the stage.
   * @param {number} clientX - Horizontal position in the viewport.
   * @param {number} clientY - Vertical position in the viewport.
   * @returns {{x: number, y: number}}
   */
  #focusOf(clientX, clientY) {
    const slide = this.#activeSlide();
    if (!slide) return { x: 0, y: 0 };

    const bounds = slide.getBoundingClientRect();
    return {
      x: clientX - bounds.left - bounds.width / 2,
      y: clientY - bounds.top - bounds.height / 2,
    };
  }

  /**
   * Routes the rest of a gesture to the slide holding the picture. Capturing on the
   * carousel instead retargets the closing click there too, and a click that does not land
   * inside a slide counts as a click on the backdrop — which shut the overlay the moment a
   * magnified picture was clicked or dragged.
   * @param {number} pointerId - The pointer to follow.
   */
  #capturePointer(pointerId) {
    const slide = this.#activeSlide();
    if (slide instanceof HTMLElement) slide.setPointerCapture(pointerId);
  }

  /**
   * The spread and midpoint of the two fingers currently down.
   * @returns {{spread: number, x: number, y: number}}
   */
  #pinchOf() {
    const [first, second] = [...this.#pointers.values()];

    return {
      spread: Math.hypot(second.x - first.x, second.y - first.y),
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2,
    };
  }

  /**
   * Magnifies by however much the fingers have spread since the last move, and follows
   * them as they travel across the picture.
   */
  #applyPinch() {
    const previous = this.#pinch;
    const current = this.#pinchOf();

    this.#pinch = current;
    if (!previous || previous.spread === 0) return;

    const focus = this.#focusOf(current.x, current.y);
    this.#zoomTo(this.#scale * (current.spread / previous.spread), focus.x, focus.y);

    this.#panX += current.x - previous.x;
    this.#panY += current.y - previous.y;
    this.#render();
  }

  /**
   * @param {PointerEvent} event - A press landing on the carousel.
   */
  #handlePointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.product-media-container--image')) return;
    if (!this.#activeImage()) return;

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size === 2) {
      this.#pinch = this.#pinchOf();
      this.#capturePointer(event.pointerId);
      return;
    }

    if (this.#pointers.size > 2) return;

    this.#pressedAt = { x: event.clientX, y: event.clientY };
    this.#dragged = false;

    // A phone has no cursor to click with, so on touch it is the second of two quick taps
    // that steps the magnification.
    if (event.pointerType === 'touch') {
      if (event.timeStamp - this.#lastTapAt < DOUBLE_TAP_MS) {
        this.#lastTapAt = 0;
        this.#stepZoom(event);
      } else {
        this.#lastTapAt = event.timeStamp;
      }
    }

    if (this.#scale > 1) {
      this.#drag = { x: event.clientX, y: event.clientY, panX: this.#panX, panY: this.#panY };
      this.#capturePointer(event.pointerId);
      this.setAttribute('data-panning', '');
    }
  };

  /**
   * @param {PointerEvent} event - A pointer moving with a button or finger down.
   */
  #handlePointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size >= 2) {
      event.preventDefault();
      this.#dragged = true;
      this.#applyPinch();
      return;
    }

    const travelled = this.#pressedAt
      ? Math.hypot(event.clientX - this.#pressedAt.x, event.clientY - this.#pressedAt.y)
      : 0;
    if (travelled > TAP_SLOP) this.#dragged = true;

    // At fitted size there is nothing to pan, and the move is left alone so the carousel
    // can carry the drag through to the next image as it always has.
    if (!this.#drag) return;

    event.preventDefault();
    this.#panX = this.#drag.panX + (event.clientX - this.#drag.x);
    this.#panY = this.#drag.panY + (event.clientY - this.#drag.y);
    this.#render();
  };

  /**
   * @param {PointerEvent} event - A pointer being lifted or taken away.
   */
  #handlePointerEnd = (event) => {
    if (!this.#pointers.delete(event.pointerId)) return;

    // Coming out of a pinch: re-anchor the finger still down so the picture does not jump.
    if (this.#pointers.size === 1) {
      const [remaining] = [...this.#pointers.values()];

      this.#pinch = null;
      this.#drag = this.#scale > 1 ? { x: remaining.x, y: remaining.y, panX: this.#panX, panY: this.#panY } : null;
      return;
    }

    if (this.#pointers.size > 0) return;

    const clicked = event.type === 'pointerup' && event.pointerType === 'mouse' && !this.#dragged;

    this.#pinch = null;
    this.#drag = null;
    this.#pressedAt = null;
    this.#dragged = false;
    this.removeAttribute('data-panning');

    if (clicked) this.#stepZoom(event);
  };

  /**
   * @param {WheelEvent} event - A wheel or trackpad gesture over the carousel.
   */
  #handleWheel = (event) => {
    // A trackpad pinch reaches the page as ctrl + wheel, and that is the only wheel
    // gesture that magnifies.
    if (event.ctrlKey) {
      if (!this.#activeImage()) return;
      event.preventDefault();

      const focus = this.#focusOf(event.clientX, event.clientY);
      this.#zoomTo(this.#scale * Math.exp(-wheelDistance(event, 'deltaY') / 300), focus.x, focus.y);
      return;
    }

    // Once magnified the wheel moves around the picture. On a trackpad a two-finger scroll
    // is the gesture that comes to hand for this, and holding the button down to drag as
    // far as the edge of a label is far more effort than reading it is worth.
    if (this.#scale > 1) {
      event.preventDefault();
      this.#panX -= wheelDistance(event, 'deltaX');
      this.#panY -= wheelDistance(event, 'deltaY');
      this.#render();
      return;
    }

    // At fitted size a sideways swipe is how the carousel moves between images, so it is
    // left alone. A vertical one has nothing to reach behind the overlay.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    event.preventDefault();
  };

  /**
   * @param {DragEvent} event - The browser offering to drag the picture away.
   */
  #handleDragStart = (event) => {
    // A picture can be dragged out of the page by default, and the browser takes the
    // gesture over a few pixels in. Panning a magnified picture died almost as soon as it
    // started because of it, so the offer is declined.
    event.preventDefault();
  };

  /**
   * @param {TouchEvent} event - A touch gesture travelling over the carousel.
   */
  #handleTouchMove = (event) => {
    // Two fingers on the picture is a pinch, so the carousel must not scroll as well. Once
    // magnified the picture opts out of browser gestures itself and this is moot.
    if (this.#pointers.size >= 2) event.preventDefault();
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    this.#showIndex(index);
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // showModal() alone doesn't lock background scroll, so do it explicitly.
      document.documentElement.setAttribute('scroll-lock', '');

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      // Sync the active thumbnail highlight to the opened image (e.g. when the
      // "See more" CTA opens at a non-zero index with no view transition).
      this.selectThumbnail(index, { behavior: 'instant' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails, gallery } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media, gallery);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#showIndex(activeIndex);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    this.#resetZoom();

    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media, this.refs.gallery);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    document.documentElement.removeAttribute('scroll-lock');
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Closes the dialog when the user clicks the overlay around the image, but not
   * when interacting with the image, the nav arrows, the thumbnails, or media players.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  closeFromOverlay(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (
      target.closest('.product-media-container, .dialog-zoomed-gallery__nav, .dialog-thumbnails-list-container, .close-button')
    ) {
      return;
    }

    // A press on the picture can arrive reporting the dialog itself as its target rather
    // than the image — a magnified image and a captured pointer both do this — and the
    // check above then reads it as a press on the surround and shuts the overlay under
    // the shopper. Where the press actually landed does not have that problem, so a press
    // within the carousel is left alone whatever element it claims to have hit.
    const { gallery } = this.refs;
    if (gallery) {
      const bounds = gallery.getBoundingClientRect();
      const insideGallery =
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom;
      if (insideGallery) return;
    }

    this.close();
  }

  /**
   * Advances to the next image, wrapping around to the first.
   */
  next() {
    this.#step(1);
  }

  /**
   * Goes to the previous image, wrapping around to the last.
   */
  previous() {
    this.#step(-1);
  }

  /**
   * Moves the active image by an offset, wrapping around at either end.
   * @param {number} offset - The number of images to move by (e.g. 1 or -1).
   */
  #step(offset) {
    const count = this.refs.media.length;
    if (count < 2) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const index = (this.#activeIndex + offset + count) % count;
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#showIndex(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * How far a wheel gesture travelled along one axis, in pixels. A mouse wheel reports its
 * travel in lines or pages rather than pixels, which would otherwise barely move anything.
 * @param {WheelEvent} event - The wheel event.
 * @param {'deltaX' | 'deltaY'} axis - The axis to measure.
 * @returns {number}
 */
function wheelDistance(event, axis) {
  return event.deltaMode === WheelEvent.DOM_DELTA_PIXEL ? event[axis] : event[axis] * 16;
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {Element | null} [root] - The scroll container to measure visibility against (defaults to the viewport).
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements, root = null) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        root,
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
